import { apiFetch } from "@/lib/api";
import { hubApiUrl } from "@/lib/hubApiOrigin";
import type { HubPresenceRoute } from "@/lib/hubPresenceRoute";

/** Active window — matches server ACTIVE_MS. */
export const HUB_PRESENCE_ACTIVE_MS = 5 * 60 * 1000;

export type HubPresenceState = "active" | "idle";

export type HubPresenceUpdateResult = {
  routeKey: string;
  pathname: string;
  pageLabel: string;
  moduleKey: string | null;
  entityType: string | null;
  entityId: string | null;
  isVisible: boolean;
  firstSeenAt: string;
  lastInteractionAt: string;
  lastHeartbeatAt: string;
  presenceState: HubPresenceState;
  navigablePath: string;
  heartbeatIntervalMs: number;
  viewers?: HubPresenceViewer[];
};

export type HubPresenceViewer = {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  presenceState: HubPresenceState;
  isVisible: boolean;
  tabCount: number;
  lastInteractionAt: string;
  lastHeartbeatAt: string;
};

export type HubPresenceCurrentResponse = {
  routeKey: string;
  viewers: HubPresenceViewer[];
};

export type HubPresencePageEntry = {
  routeKey: string;
  pathname: string;
  navigablePath: string;
  pageLabel: string;
  moduleKey: string | null;
  entityType: string | null;
  entityId: string | null;
  isVisible: boolean;
  tabCount: number;
  lastInteractionAt: string;
  lastHeartbeatAt: string;
  presenceState: HubPresenceState;
};

export type HubPresenceUserGroup = {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  presenceState: HubPresenceState;
  pages: HubPresencePageEntry[];
};

export type HubPresenceAllResponse = {
  users: HubPresenceUserGroup[];
};

export function hubPresenceCurrentQueryKey(userId: string, routeKey: string) {
  return ["hub-presence", userId, "current", routeKey] as const;
}

export function hubPresenceAllQueryKey(userId: string) {
  return ["hub-presence", userId, "all"] as const;
}

function presenceUpdateBody(input: {
  tabId: string;
  route: HubPresenceRoute;
  isVisible: boolean;
  activitySinceLastHeartbeat: boolean;
  presenceState: HubPresenceState;
}) {
  return {
    tabId: input.tabId,
    routeKey: input.route.routeKey,
    pathname: input.route.pathname,
    pageLabel: input.route.pageLabel,
    moduleKey: input.route.moduleKey,
    entityType: input.route.entityType,
    entityId: input.route.entityId,
    navigablePath: input.route.navigablePath,
    isVisible: input.isVisible,
    activitySinceLastHeartbeat: input.activitySinceLastHeartbeat,
    presenceState: input.presenceState,
  };
}

/** Push route, visibility, or idle/active changes. Liveness is a periodic POST heartbeat. */
export async function postHubPresenceUpdate(input: {
  tabId: string;
  route: HubPresenceRoute;
  isVisible: boolean;
  activitySinceLastHeartbeat: boolean;
  presenceState: HubPresenceState;
}): Promise<HubPresenceUpdateResult> {
  return apiFetch<HubPresenceUpdateResult>("/api/hub/presence/update", {
    method: "POST",
    body: presenceUpdateBody(input),
    cache: "no-store",
  });
}

/** Keep the tab row alive without opening a per-tab EventSource. */
export async function postHubPresenceHeartbeat(input: {
  tabId: string;
  route: HubPresenceRoute;
  isVisible: boolean;
  activitySinceLastHeartbeat: boolean;
  presenceState: HubPresenceState;
}): Promise<HubPresenceUpdateResult> {
  return apiFetch<HubPresenceUpdateResult>("/api/hub/presence/heartbeat", {
    method: "POST",
    body: presenceUpdateBody(input),
    cache: "no-store",
  });
}

export async function postHubPresenceClose(input: {
  tabId: string;
  keepalive?: boolean;
}): Promise<{ closed: boolean }> {
  return apiFetch<{ closed: boolean }>("/api/hub/presence/close", {
    method: "POST",
    body: { tabId: input.tabId },
    cache: "no-store",
    keepalive: input.keepalive === true,
  });
}

/**
 * Best-effort close during `pagehide` / unload. Uses a snapshot access token because
 * async session lookup may not finish before the page tears down.
 */
export function sendHubPresenceCloseBeacon(input: {
  tabId: string;
  accessToken: string | null;
}): void {
  if (typeof fetch !== "function") {
    return;
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (input.accessToken != null && input.accessToken.trim() !== "") {
    headers.Authorization = `Bearer ${input.accessToken}`;
  }
  try {
    void fetch(hubApiUrl("/api/hub/presence/close"), {
      method: "POST",
      headers,
      body: JSON.stringify({ tabId: input.tabId }),
      keepalive: true,
    });
  } catch {
    /* page may already be torn down */
  }
}

export async function fetchHubPresenceCurrent(
  routeKey: string,
  signal?: AbortSignal,
): Promise<HubPresenceCurrentResponse> {
  return apiFetch<HubPresenceCurrentResponse>("/api/hub/presence/current", {
    query: { routeKey },
    cache: "no-store",
    signal,
  });
}

export type HubPresencePageEntitiesResponse = {
  pathname: string;
  entityType: string;
  entities: Record<string, HubPresenceViewer[]>;
};

export function hubPresencePageEntitiesQueryKey(
  userId: string,
  pathname: string,
  entityType: string,
) {
  return ["hub-presence", userId, "page-entities", pathname, entityType] as const;
}

export async function fetchHubPresencePageEntities(
  pathname: string,
  entityType: string,
  signal?: AbortSignal,
): Promise<HubPresencePageEntitiesResponse> {
  return apiFetch<HubPresencePageEntitiesResponse>("/api/hub/presence/page-entities", {
    query: { pathname, entityType },
    cache: "no-store",
    signal,
  });
}

export async function fetchHubPresenceAll(): Promise<HubPresenceAllResponse> {
  return apiFetch<HubPresenceAllResponse>("/api/hub/presence/all", {
    cache: "no-store",
  });
}
